import { Command, CommandResult } from '@/_core/cqrs/types/commands';
import { JiraIssueTypeModel, TrelloListModel } from '../queries';

export class ConnectToTrelloAndGetBoards extends Command<ConnectToTrelloAndGetBoardsResult> {
    constructor(public StorymapId: string, public Token: string) {
        super('ConnectToTrelloAndGetBoards');
    }
}

export class LinkTrelloBoard extends Command<CommandResult> {
    constructor(public StorymapId: string, public BoardId: string, public BoardShortLink: string, public BoardName: string) {
        super('LinkTrelloBoard');
    }
}

export class SaveTrelloSettings extends Command<SaveTrelloSettingsResult> {
    constructor(
        public StorymapId: string,
        public ExportingListId: string,
        public SynchronizeNameChanges: boolean,
        public SynchronizeDescriptionChanges: boolean,
        public EnableImportOptionOnBoard: boolean,
        public IgnoreArchivedCards: boolean,
        public AddStoriesOnBoardLink: boolean,
        public SynchronizeEstimationChanges: boolean,
        public StatusMappings: TrelloStatusMappingModel[],
        public ExportPosition: TrelloExportPostion
    ) {
        super('SaveTrelloSettings');
    }
}

export enum TrelloExportPostion {
    Bottom = 1,
    Top = 2,
}

export class DisconnectFromTrello extends Command<CommandResult> {
    constructor(public StorymapId: string) {
        super('DisconnectFromTrello');
    }
}

export class DisconnectFromGitHub extends Command<CommandResult> {
    constructor(public StorymapId: string) {
        super('DisconnectFromGitHub');
    }
}

export class DisconnectFromJira extends Command<CommandResult> {
    constructor(public StorymapId: string) {
        super('DisconnectFromJira');
    }
}

export class ToggleIntegrationEnabled extends Command<ToggleIntegrationEnabledResult> {
    /**
     * DeferPull (GitHub enable only): the inbound pull is left to RunGitHubPullStep commands.
     * Other callers leave it undefined, so they send the same JSON as before.
     */
    constructor(public StorymapId: string, public DeferPull?: boolean) {
        super('ToggleIntegrationEnabled');
    }
}

export interface ToggleIntegrationEnabledResult extends CommandResult {
    IsEnableExecuted: boolean;
    PullRequired?: boolean;
    /** The enabled / disabled flag itself is stored (also when a following in-request pull failed). */
    IsPersisted?: boolean;
    PullOutcome?: IntegrationPullOutcome;
}

/** Whether a save or enable ran the tracker's in-request inbound pull and whether it ran to its end. */
export enum IntegrationPullOutcome {
    NotRun = 0,
    Completed = 1,
    Failed = 2,
}

export class DeleteIncompleteIntegration extends Command<CommandResult> {
    constructor(public StorymapId: string) {
        super('DeleteIncompleteIntegration');
    }
}

export class RenewTrelloAccessToken extends Command<CommandResult> {
    constructor(public StorymapId: string, public Token: string) {
        super('RenewTrelloAccessToken');
    }
}

export class RenewGitHubAccessToken extends Command<CommandResult> {
    constructor(public StorymapId: string, public PersonalAccessToken: string) {
        super('RenewGitHubAccessToken');
    }
}

export class ConnectToGitHubAndGetRepos extends Command<ConnectToGitHubAndGetReposResult> {
    constructor(public StorymapId: string, public PersonalAccessToken: string) {
        super('ConnectToGitHubAndGetRepos');
    }
}

export class LinkGitHubRepo extends Command<CommandResult> {
    constructor(public StorymapId: string, public RepoId: string, public RepoName: string, public RepoFullName: string) {
        super('LinkGitHubRepo');
    }
}

export class SaveGitHubSettings extends Command<SaveGitHubSettingsResult> {
    constructor(
        public StorymapId: string,

        public EnableImportOptionOnBoard: boolean,
        public ImportLabelFilter: string,
        public ExportLabel: string,
        public StatusMappings: GitHubStatusMappingModel[],

        public SynchronizeDescriptionChanges: boolean,
        public SynchronizeNameChanges: boolean,
        public SynchronizeReleases: boolean,

        /** GitHub attachment access choice; null or undefined = unchanged. */
        public AttachmentAccessEnabled?: boolean | null,
        /** Leave the inbound pull to RunGitHubPullStep commands (Nextgen stepped pull). */
        public DeferPull?: boolean
    ) {
        super('SaveGitHubSettings');
    }
}

export interface SaveGitHubSettingsResult extends CommandResult {
    IsSuccessWithWarnings: boolean;
    IsEnableExecuted: boolean;
    PullRequired?: boolean;
}

/** Local OFF of the attachment access of the map's linked tracker (GitHub or Jira). No tracker call. */
export class TurnOffAttachmentAccess extends Command<CommandResult> {
    constructor(public StorymapId: string) {
        super('TurnOffAttachmentAccess');
    }
}

export class RunGitHubPullStep extends Command<RunGitHubPullStepResult> {
    /** Exactly one of CardId and ReleaseId is set. */
    constructor(public StorymapId: string, public CardId?: string | null, public ReleaseId?: string | null) {
        super('RunGitHubPullStep');
    }
}

/** One step of the enable-triggered issue description refresh: one linked card of any level. */
export class RunAttachmentRefreshStep extends Command<RunAttachmentRefreshStepResult> {
    constructor(public StorymapId: string, public CardId: string) {
        super('RunAttachmentRefreshStep');
    }
}

export enum GitHubPullStepOutcome {
    Pulled = 1,
    Unlinked = 2,
    Skipped = 3,
    NotFound = 4,
    Throttled = 5,
    Synced = 6,
    Failed = 7,
}

export enum AttachmentRefreshStepOutcome {
    Updated = 1,
    Failed = 2,
    NotAttempted = 3,
    NotFound = 4,
    Throttled = 5,
    Stopped = 6,
}

export interface RunGitHubPullStepResult extends CommandResult {
    Outcome: GitHubPullStepOutcome;
    Reason: string | null;
    WaitSeconds: number;
    SyncedReleaseId: string | null;
}

export interface RunAttachmentRefreshStepResult extends CommandResult {
    Outcome: AttachmentRefreshStepOutcome;
    Reason: string | null;
    WaitSeconds: number;
}

export interface ConnectToTrelloAndGetBoardsResult extends CommandResult {
    Boards: TrelloBoardModel[];
}

export class SaveJiraSettings extends Command<EnableIntegrationResult> {
    /** Jira attachment access choice; sent only when defined (null or undefined = unchanged). */
    public AttachmentAccessEnabled?: boolean | null;

    constructor(public StorymapId: string, public Settings: SaveJiraSettingsArg, attachmentAccessEnabled?: boolean | null) {
        super('SaveJiraSettings');
        if (attachmentAccessEnabled !== undefined && attachmentAccessEnabled !== null) {
            this.AttachmentAccessEnabled = attachmentAccessEnabled;
        }
    }
}

export class ConnectToJiraInitateOAuth extends Command<ConnectToJiraInitateOAuthResult> {
    constructor(public StorymapId: string, public IsNextGen: boolean = true) {
        super('ConnectToJiraInitateOAuth');
    }
}

export class ConnectToJiraWithStoredTokenAndGetProjects extends Command<ConnectToJiraAndGetProjectsResult> {
    constructor(public StorymapId: string) {
        super('ConnectToJiraWithStoredTokenAndGetProjects');
    }
}

export class LinkJiraProjectOnNextGen extends Command<LinkJiraProjectOnNextGenResult> {
    constructor(public StorymapId: string, public ProjectId: string, public ProjectKey: string, public ProjectName: string) {
        super('LinkJiraProjectOnNextGen');
    }
}

export class ReplaceJiraExpiredTokenWithStoredToken extends Command<CommandResult> {
    constructor(public StorymapId: string) {
        super('ReplaceJiraExpiredTokenWithStoredToken');
    }
}

export class InitiateJiraOAuthForImpersonation extends Command<CommandResult> {
    constructor(public StorymapId: string, public IsNextGen = true) {
        super('InitiateJiraOAuthForImpersonation');
    }
}

export class ConnectToJiraWithCorporate extends Command<CommandResult> {
    constructor(public StorymapId: string) {
        super('ConnectToJiraWithCorporate');
    }
}

export class EnableIntegrationResult {
    public IsEnableExecuted: boolean;
    public IsSuccess: boolean;
    public IsIntegrationEnabled: boolean;
    public Message: string;
    public IsImpersonationChanged: boolean;
    /** The action's own change is stored (settings and choice, or the enabled flag). */
    public IsPersisted?: boolean;
    public PullOutcome?: IntegrationPullOutcome;
}

class CustomExportValueModel {
    public FieldKey: string;
    public Values: string[];
}

export interface JiraStatusMappingStatusModel {
    JiraStatus: JiraStatusModel;
    Status?: number;
}

export interface JiraStatusModel {
    Id: string;
    Name: string;
}

export interface JiraStatusMappingModel {
    IssueTypeId: string;
    IssueTypeName: string;
    StoryPointFieldId: string;
    Statuses: JiraStatusMappingStatusModel[];
    IsSyncEnabled: boolean;
    IsEpic: boolean;
    EpicNameFieldId: string;
    HasReleaseField: boolean;
}

class SaveJiraSettingsArg {
    public ExportingIssueTypeId: string;
    public ExportingIssueTypeStoryPointFieldId: string;
    public ExportingIssueTypeIsEpic: boolean;
    public EpicLinkFieldId: string;
    public ActivityLevelExportingItemTypeId: string;
    public TaskLevelExportingItemTypeId: string;
    public IsStructureSyncEnabled: boolean;
    public StatusMappings: JiraStatusMappingModel[];
    public SynchronizeNameChanges: boolean;
    public SynchronizeDescriptionChanges: boolean;
    public SynchronizeEstimationChanges: boolean;
    public AutoSyncReleases: boolean;
    public EnableImportOptionOnBoard: boolean;
    public IgnoreClosedIssues: boolean;
    public EnableDescriptionFormatConversion: boolean;
    public HasAdvancedImportFilter: boolean;
    public AdvancedImportFilter?: string;
    public CustomExportValues: CustomExportValueModel[];
    public IsImpersonationEnabled: boolean;
    public AddStoriesOnBoardLink: boolean;
    public IssueTypeAbbreviations: JiraIssueTypeAbbreviationModel[];
}

export interface JiraIssueTypeAbbreviationModel {
    IssueTypeId: string;
    Abbreviation: string;
}

export interface TrelloBoardModel {
    Id: string;
    Name: string;
    Shortlink: string;
}

export interface TrelloStatusMappingModel {
    TrelloList: TrelloListModel;
    Status: number;
}

export interface SaveTrelloSettingsResult extends CommandResult {
    IsEnableExecuted: boolean;
}

export interface ConnectToGitHubAndGetReposResult extends CommandResult {
    Model: { Repos: GithubRepoModel[] };
}

export interface GithubRepoModel {
    Id: string;
    Name: string;
    FullName: string;
}

export interface GitHubStatusMappingModel {
    GitHubStatus: GitHubIssueStatus;
    GitHubLabel: string;
    StoriesOnBoardStatus: number;
}

export interface ConnectToJiraInitateOAuthResult extends CommandResult {
    Url: string;
}

export interface ConnectToJiraAndGetProjectsResult extends CommandResult {
    Model: JiraProjectsWithAuthenticatedAccountModel;
}

export interface JiraProjectModel {
    Id: string;
    Key: string;
    Name: string;
    IsNextGenProject: boolean;
}

export interface JiraAuthenticatedAccountModel {
    IsIdentified: boolean;
    DisplayName: string;
    EmailAddress: string;
    IsCorporate: boolean;
}

export interface JiraProjectsWithAuthenticatedAccountModel {
    Projects: JiraProjectModel[];
    AuthenticatedAccount: JiraAuthenticatedAccountModel;
}

export enum JiraProjectPermissionPreflightStatus {
    Passed = 0,
    Failed = 1,
    Unavailable = 2,
}

export interface JiraProjectPermissionPreflightResult {
    Status: JiraProjectPermissionPreflightStatus | keyof typeof JiraProjectPermissionPreflightStatus;
    Message: string;
    MissingPermissionKeys: string[];
    AuthenticatedAccount: JiraAuthenticatedAccountModel;
    ProjectId: string;
    ProjectKey: string;
    ProjectName: string;
}

export interface LinkJiraProjectOnNextGenResult extends CommandResult {
    IssueTypes: JiraIssueTypeModel[];
    PermissionPreflight?: JiraProjectPermissionPreflightResult;
}

export enum GitHubIssueStatus {
    NotSpecified = 0,
    Open = 1,
    Closed = 2,
}
